// Get risks validated
import { createAsyncThunk } from "@reduxjs/toolkit"
import { fetchBackend } from "@/core/apis/backend"
import riskStore from "@/features/risks/entities/risk/model/risk.store"
import { DEFAULT_RISK_SCALES, Risk } from "@/features/risks/entities/risk/model/risk.types"
import unitStore from "@/features/risks/entities/unit/model/unit.store"
import { formatRisk } from "@/features/risks/entities/risk/model/risk.formatter"
import { RootState } from "@/core/root-reducer"
import organizationStore from "@/features/adm/entities/organization/model/organization.store"
import establishmentStore from "@/features/adm/entities/establishment/model/establishment.store"

//FETCH RISK HISTORY
//> For the side panel "Historique du risque"
export const fetchPublicRiskHistory = createAsyncThunk(
  "public/risk/history",
  async ({ riskId, establishmentId }: { riskId: string; establishmentId: string }, { getState }) => {
    const state = getState() as RootState

    const res = await fetchBackend({
      url: `/public/risk/${riskId}/history`,
      data: {
        establishmentId
      }
    })

    if (res.error) {
      console.error("❌ fetchPublicRiskHistory error:", res.error)
      return []
    }

    const session = state._session
    const organization = state.organization.active

    // Retourne la liste formatée directement
    return res.map((h) => formatRisk(h, session, organization))
  }
)

//FETCH PUBLIC RISKS
//> Fetch risks and units for the public link page
export const fetchPublicAndSet = createAsyncThunk(
  "public/risks",
  async (
    { date, establishmentId, organizationId }: { date: Date | null; establishmentId?: string; organizationId?: string },
    { dispatch, getState }
  ) => {
    const state = getState() as RootState

    // Use provided IDs or fallback to store
    const estId = establishmentId || establishmentStore.selectors.active(state)?.id
    const orgId = organizationId || organizationStore.selectors.active(state)?.id

    dispatch(riskStore.actions.setStatus("STATUS_LOADING"))

    const response = await fetchBackend({
      url: `/public/risks`,
      method: "GET",
      data: { establishmentId: estId, organizationId: orgId, date }
    })

    if (response.error) {
      dispatch(organizationStore.actions.edit({ key: "severityScale", value: DEFAULT_RISK_SCALES.severityScale }))
      dispatch(organizationStore.actions.edit({ key: "frequencyScale", value: DEFAULT_RISK_SCALES.frequencyScale }))
      dispatch(organizationStore.actions.edit({ key: "preventionScale", value: DEFAULT_RISK_SCALES.preventionScale }))
      dispatch(
        organizationStore.actions.edit({ key: "residualRiskScale", value: DEFAULT_RISK_SCALES.residualRiskScale })
      )
      dispatch(organizationStore.actions.edit({ key: "riskAttributes", value: [] }))

      dispatch(riskStore.actions.setList([]))
      dispatch(unitStore.actions.setList([]))
      dispatch(riskStore.actions.setStatus("STATUS_LOAD_ERROR"))
    } else {
      const { attributes, riskScales, risks, units, establishment, organization } = response
      const { severityScale, frequencyScale, preventionScale, residualRiskScale } = riskScales

      // Set establishment and organization in store
      if (establishment) {
        dispatch(establishmentStore.actions.setActive(establishment))
      }
      if (organization) {
        dispatch(organizationStore.actions.setActive(organization))
      }

      dispatch(organizationStore.actions.edit({ key: "severityScale", value: severityScale }))
      dispatch(organizationStore.actions.edit({ key: "frequencyScale", value: frequencyScale }))
      dispatch(organizationStore.actions.edit({ key: "preventionScale", value: preventionScale }))
      dispatch(organizationStore.actions.edit({ key: "residualRiskScale", value: residualRiskScale }))
      dispatch(organizationStore.actions.edit({ key: "riskAttributes", value: attributes }))

      dispatch(riskStore.actions.setList(risks))
      dispatch(unitStore.actions.setList(units))
      dispatch(riskStore.actions.setStatus("STATUS_LOADED"))
    }

    return response
  }
)

//Fetch Risks with Units
export const fetchWithUnitsAndSet = createAsyncThunk(
  "risks/with-units",
  async (context: "alerts" | "contributions", { dispatch, getState }) => {
    const state = getState() as RootState
    const establishment = establishmentStore.selectors.active(state)

    //Load data
    dispatch(riskStore.actions.setStatus("STATUS_LOADING"))
    dispatch(riskStore.actions.clearSelected())
    dispatch(unitStore.actions.clearSelected())

    // Load units
    const units = await fetchBackend({
      url: `/units?establishmentId=${establishment.id}`,
      data: { establishmentId: establishment.id }
    })

    // Load risks
    const risks = await fetchBackend({
      url: `/risks/${context}?establishmentId=${establishment.id}`
    })

    if (risks.error || units.error) {
      dispatch(riskStore.actions.setList([]))
      dispatch(riskStore.actions.setStatus("STATUS_LOAD_ERROR"))
      return []
    }

    // Update store (for units, only selected)
    dispatch(riskStore.actions.setList(risks))
    dispatch(unitStore.actions.setList(units.error ? [] : units))
    dispatch(riskStore.actions.setStatus("STATUS_LOADED"))
    return risks
  }
)

//FETCH ALL VALIDATED RISKS
//> For the "document" page in order to export the current list
//> For the "units" page in order to get heatmap risks in the node
export const fetchValidatedAndSet = createAsyncThunk(
  "risks/validated",
  async (date: Date | null, { dispatch, getState }) => {
    const state = getState() as RootState
    const establishment = establishmentStore.selectors.active(state)

    //Load data
    dispatch(riskStore.actions.setStatus("STATUS_LOADING"))
    const risks = await fetchBackend({
      url: "/risks/validated",
      data: { establishmentId: establishment.id, date }
    })

    //Handle error
    if (risks.error) {
      dispatch(riskStore.actions.setList([]))
      dispatch(riskStore.actions.setStatus("STATUS_LOAD_ERROR"))
    }
    //Handle success success
    else {
      //Update list of risks
      dispatch(riskStore.actions.setList(risks))

      //Mark as loaded
      dispatch(riskStore.actions.setStatus("STATUS_LOADED"))
    }

    return risks
  }
)

// SUGGEST ONE RISK
export const suggestOne = createAsyncThunk(
  "risk/suggest-one",
  async (
    payload: {
      UnitId: string
      attributes: Record<string, string>
      targetAttr?: string
      rps?: boolean
    },
    { getState }
  ) => {
    const suggested = await fetchBackend({
      url: "/risk/suggest",
      method: "POST",
      data: { risk: payload },
      timeout: 60000
    })

    if (suggested.error) {
      return null
    } else {
      const state = getState() as RootState
      const session = state._session
      const organization = state.organization.active

      const formated = formatRisk(suggested, session, organization)
      return formated
    }
  }
)

//SUGGEST RISKS FOR A UNIT
//> Ask backend (AI) to suggest risks for a given unit
//SUGGEST RISKS FOR A UNIT
export const suggestForUnit = createAsyncThunk(
  "risks/suggest",
  async ({ id, name, description, rps = false }: { id: string; name: string; description: string; rps?: boolean }, { dispatch, getState }) => {
    dispatch(riskStore.actions.setStatus("STATUS_LOADING"))

    const state = getState() as RootState
    const establishment = establishmentStore.selectors.active(state)

    const risks = await fetchBackend({
      url: "/risks/suggest",
      method: "POST",
      data: { unit: { id, name, description }, rps, establishmentId: establishment?.id },
      timeout: 60000
    })

    if (risks.error) {
      dispatch(riskStore.actions.setStatus("STATUS_LOAD_ERROR"))
      return []
    }

    dispatch(riskStore.actions.setStatus("STATUS_LOADED"))

    const session = state._session
    const organization = state.organization.active

    const formated = risks.map((r) => formatRisk(r, session, organization))
    dispatch(riskStore.actions.setList(formated))

    return risks
  }
)

//Update already validated risks
export const updateValidated = createAsyncThunk(
  "risks/update-validated",
  async (risks: Risk[], { dispatch, getState }) => {
    const state = getState() as RootState
    const establishment = establishmentStore.selectors.active(state)

    //Start loading
    dispatch(riskStore.actions.setStatus("STATUS_SAVING"))

    //Load data
    const response = await fetchBackend({
      url: `/risks/validated`,
      method: "POST",
      data: { establishmentId: establishment.id, risks }
    })

    //Handle answer
    if (response.error) {
      dispatch(riskStore.actions.setStatus("STATUS_SAVE_ERROR"))
    } else {
      dispatch(riskStore.actions.setStatus("STATUS_SAVED"))

      //When there is at least one update concerning me
      if (risks.length) {
        dispatch(riskStore.actions.setActive(response[0]))
      }
    }

    return null
  }
)

// CREATE FROM JOB (AI-powered, no circular deps)
export const createFromJob = createAsyncThunk(
  "risk/create-from-job",
  async (
    {
      unitId,
      title,
      origin
    }: {
      unitId: string
      title: string
      origin: string
    },
    { dispatch, getState }
  ) => {
    const state = getState() as RootState
    const attributesList = state.organization.active.riskAttributes
    const establishment = establishmentStore.selectors.active(state)

    // Construire les attributs
    const attributes: Record<string, string> = {}
    attributesList.forEach((attr, i) => {
      if (i === 0) attributes[attr] = title
      else if (i === 1) attributes[attr] = origin
      else attributes[attr] = ""
    })

    const aiResponse = await fetchBackend({
      url: "/risk/suggest",
      method: "POST",
      data: {
        establishmentId: establishment.id,
        risk: {
          UnitId: unitId,
          attributes,
          targetAttr: null
        }
      },
      timeout: 60000
    })

    const risk = {
      ...aiResponse,
      attributes: aiResponse.attributes ?? attributes,
      severity: aiResponse.severity,
      frequency: aiResponse.frequency,
      prevention: aiResponse.prevention,
      UnitId: unitId
    }

    const riskFormated = formatRisk(risk, state._session, state.organization.active)

    // Créer le risque via la route backend
    const created = await fetchBackend({
      url: "/risk",
      method: "POST",
      data: riskFormated
    })

    if (created?.error) {
      console.error("❌ Failed to create risk", created.error)
      throw new Error("risk_create_failed")
    }

    //  Ajouter au store
    dispatch(riskStore.actions.addMany([created]))

    return created
  }
)

//IMPORT RISKS
//> Import risks from file and reload all data
export const importRisks = createAsyncThunk(
  "risks/import",
  async (
    {
      establishmentId,
      risks,
      units,
      replace,
      riskAttributes
    }: {
      establishmentId: string
      risks: any[]
      units: any[]
      replace: boolean
      riskAttributes: string[]
    },
    { dispatch }
  ) => {
    //Step 1: Import risks and units
    const response = await fetchBackend({
      url: "/risks/import",
      method: "POST",
      data: {
        establishmentId,
        risks,
        units,
        replace,
        riskAttributes
      }
    })

    if (response.error) {
      throw new Error("import_failed")
    }

    //Step 2: Update organization attributes
    dispatch(organizationStore.actions.edit({ key: "riskAttributes", value: riskAttributes }))
    dispatch(organizationStore.actions.sync())

    //Step 3: Reload units and risks (in parallel without waiting)
    dispatch(unitStore.thunks.fetchAndSet())
    dispatch(fetchValidatedAndSet(null))

    return response.data
  }
)
